/**
 * Damage & Knockback — explosion damage % accumulation with Smash-style scaled knockback.
 */

import { KNOCKBACK, PHYSICS, CLASSIC, WEAPON_CHARGE } from '../constants';
import type { GameState } from '../types';
import type { WeaponDef } from '../weapons';
import { cancelGrapple } from './grapple';
import { mirrorDIFreeze } from '../physics/transitions';
import { modeIsClassic, modeUsesShotTiers } from './mode';
import { lookupTank } from '../tanks';
import { getGlitchKnockbackMult } from './glitch';

export function applyExplosionDamage(
  state: GameState,
  x: number,
  y: number,
  blastRadius: number,
  baseDamage: number,
  firedBy: number,
): { hitPlayers: number[]; damages: number[] } {
  const hitPlayers: number[] = [];
  const damages: number[] = [];

  for (let i = 0; i < state.players.length; i++) {
    const p = state.players[i];
    if (!p.alive) continue;
    if (p.dashImmune) continue; // Duelist iaijutsu dash immunity
    if (p.respawnInvulnFrames > 0) continue; // Post-respawn invulnerability

    const dx = p.x - x;
    const dy = p.y - y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    const ecb = lookupTank(p.tankId).ecbRadius ?? PHYSICS.ECB_RADIUS;
    if (dist < blastRadius + ecb) {
      // Linear falloff from center
      const effectiveDist = Math.max(0, dist - ecb);
      const falloff = 1 - effectiveDist / blastRadius;
      const damage = Math.max(0, baseDamage * falloff);

      // Classic mode: subtract from HP; standard modes: accumulate damage %
      if (modeIsClassic(state.modeState.mode)) {
        p.hp = Math.max(0, p.hp - damage);
      } else {
        p.damagePercent += damage;
      }
      hitPlayers.push(i);
      damages.push(damage);

      // Match stat tracking
      if (firedBy >= 0 && firedBy !== i && state.matchStats) {
        state.matchStats.damageDealt[firedBy] += damage;
        state.matchStats.damageTaken[i] += damage;
        if (damage > state.matchStats.bestSingleHit[firedBy]) {
          state.matchStats.bestSingleHit[firedBy] = damage;
        }
        p.lastHitBy = firedBy;
      }

      // Weapon charge fill (only for cross-player hits in shot-tier modes)
      if (firedBy >= 0 && firedBy !== i && modeUsesShotTiers(state.modeState.mode)) {
        // Attacker: fill from damage dealt (skip if they fired S2/S3 this turn)
        const attacker = state.players[firedBy];
        if (attacker?.alive && !attacker.chargeDrainedThisTurn) {
          const atkSize = lookupTank(attacker.tankId).chargeSize ?? 100;
          attacker.weaponCharge = Math.min(atkSize, attacker.weaponCharge + Math.round(damage * WEAPON_CHARGE.DAMAGE_DEALT_RATE));
        }
        // Defender: fill from damage taken (comeback)
        const defSize = lookupTank(p.tankId).chargeSize ?? 100;
        p.weaponCharge = Math.min(defSize, p.weaponCharge + Math.round(damage * WEAPON_CHARGE.DAMAGE_TAKEN_RATE));
      }
    }
  }

  return { hitPlayers, damages };
}

export function applyBlastKnockback(
  state: GameState,
  x: number,
  y: number,
  blastRadius: number,
  maxForce: number,
  excludePlayer: number,
  weapon?: WeaponDef,
): number[] {
  const knockedPlayers: number[] = [];

  for (let i = 0; i < state.players.length; i++) {
    const p = state.players[i];
    if (!p.alive) continue;
    if (i === excludePlayer) continue;
    if (p.dashImmune) continue; // Duelist iaijutsu dash immunity
    if (p.respawnInvulnFrames > 0) continue; // Post-respawn invulnerability

    const dx = p.x - x;
    const dy = p.y - y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    const ecb = lookupTank(p.tankId).ecbRadius ?? PHYSICS.ECB_RADIUS;
    if (dist < blastRadius + ecb && dist > 0) {
      const effectiveDist = Math.max(0, dist - ecb);
      const falloff = 1 - effectiveDist / blastRadius;

      // Classic mode: fixed knockback; standard: scales with damage %
      const percentMult = modeIsClassic(state.modeState.mode)
        ? CLASSIC.KNOCKBACK_SCALE
        : 1 + p.damagePercent / KNOCKBACK.KBG;
      const force = maxForce * falloff * percentMult * getGlitchKnockbackMult(state);

      // Normalize direction — then apply angle bias if weapon has one
      let nx = dx / dist;
      let ny = dy / dist;
      if (weapon?.knockbackAngleBias) {
        const bias = weapon.knockbackAngleBias;
        const bx = Math.cos(bias.angle);
        const by = Math.sin(bias.angle);
        nx = nx + (bx - nx) * bias.strength;
        ny = ny + (by - ny) * bias.strength;
        const len = Math.sqrt(nx * nx + ny * ny);
        if (len > 0) { nx /= len; ny /= len; }
      }

      // Cancel grapple if this player is being pulled
      if (state.grapple.active?.playerIndex === i) {
        cancelGrapple(state);
      }

      // Disrupt armed shot on knockback (no refund)
      if (p.armedShotTier !== null) {
        p.armedShotTier = null;
        p.armedDisrupted = true;
      }

      // Knockback stacking: accumulate for stacking weapons, overwrite for others
      if (weapon?.knockbackStacking) {
        const { windowFrames, diminishingFactor } = weapon.knockbackStacking;
        const inWindow = p.kbStackHitCount > 0 &&
          (state.frameCount - p.kbStackFirstFrame) <= windowFrames;

        if (inWindow) {
          // Successive hit — accumulate with diminishing returns
          const factor = Math.pow(diminishingFactor, p.kbStackHitCount);
          p.diPendingVx += nx * force * factor;
          p.diPendingVy += ny * force * factor;
          p.kbStackHitCount++;
        } else {
          // First hit in new window
          p.diPendingVx = nx * force;
          p.diPendingVy = ny * force;
          p.kbStackHitCount = 1;
          p.kbStackFirstFrame = state.frameCount;
        }
      } else {
        // Non-stacking weapon — overwrite as before, reset stacking state
        p.diPendingVx = nx * force;
        p.diPendingVy = ny * force;
        p.kbStackHitCount = 0;
      }
      // Classic mode: cap knockback velocity to prevent launches
      if (modeIsClassic(state.modeState.mode)) {
        const mag = Math.sqrt(p.diPendingVx * p.diPendingVx + p.diPendingVy * p.diPendingVy);
        if (mag > CLASSIC.KNOCKBACK_CAP) {
          const scale = CLASSIC.KNOCKBACK_CAP / mag;
          p.diPendingVx *= scale;
          p.diPendingVy *= scale;
        }
      }

      // Round to 2dp to prevent float divergence across clients at DI threshold
      p.diPendingVx = Math.round(p.diPendingVx * 100) / 100;
      p.diPendingVy = Math.round(p.diPendingVy * 100) / 100;

      mirrorDIFreeze(p);

      knockedPlayers.push(i);
    }
  }

  return knockedPlayers;
}
